<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheListHeader :title="$t(pageTitle.title)">
          <template #actions>
            <AppDownloadCsvButton
              v-if="showCRMExportButton"
              path="enrichment-questions/downloadCsv"
              filename="pongo_export_crm.csv"
            />
          </template>
        </TheListHeader>
        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <Card class="flex-row items-center justify-between gap-6 px-4 py-4 sm:px-8">
            <CardHeader class="flex-1 p-0">
              <CardTitle class="text-sm font-semibold text-foreground sm:text-base">
                {{ $t('pages.crm.crmPage.blocks.interval.title') }}
              </CardTitle>
              <CardDescription class="text-foreground/80">
                {{ $t('pages.crm.crmPage.blocks.interval.description') }}
              </CardDescription>
            </CardHeader>
            <div class="flex items-center gap-4">
              <Button v-if="showCRMEditButton" variant="outline" size="icon" @click="interval(-1)">
                <Minus />
              </Button>
              <span class="text-base font-bold sm:text-2xl">{{ questionInterval }}</span>
              <Button v-if="showCRMEditButton" variant="outline" size="icon" @click="interval(1)">
                <Plus />
              </Button>
            </div>
          </Card>
          <div class="mt-4 flex flex-col-reverse gap-4 lg:grid lg:grid-cols-3">
            <div class="col-span-1 my-4 overflow-hidden rounded-lg border bg-white p-4 sm:p-8">
              <div class="mb-6 truncate text-xs font-medium uppercase tracking-wider text-muted-foreground sm:text-sm">
                {{ $t('pages.crm.crmPage.blocks.drag.inactive.title') }}
              </div>
              <draggable
                :list="inactive"
                item-key="id"
                class="list-group grid grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:flex-col"
                :class="[
                  inactive.length == 0
                    ? 'min-h-40 rounded-sm border-2 border-dashed border-border bg-muted px-6 py-6 pb-6 sm:px-12 sm:pb-12'
                    : 'min-h-full',
                  !showCRMEditButton ? 'pointer-events-none' : ''
                ]"
                group="people"
                ghost-class="moving"
                :animation="200"
                @dragend="save"
              >
                <template v-if="inactive.length == 0" #header>
                  <div class="col-span-2 flex flex-col items-center justify-center">
                    <div class="text-center">
                      <span class="inline-flex rounded-full bg-muted p-3 text-muted-foreground">
                        <BaseIcon name="HeroArrowDownOnSquareIconOutline" class="h-5 w-5 sm:h-6 sm:w-6" />
                      </span>
                    </div>
                    <div class="mt-4 hidden text-center text-xs text-muted-foreground sm:inline sm:text-base">
                      {{ $t('pages.crm.crmPage.blocks.drag.inactive.empty') }}
                    </div>
                  </div>
                </template>
                <template #item="{ element }">
                  <div
                    class="list-group-item cursor-move overflow-hidden rounded-lg border bg-white px-2 py-2 text-center align-middle hover:bg-accent sm:px-10 sm:py-6"
                  >
                    <div
                      v-if="element.name === 'email'"
                      class="hidden rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white sm:inline-flex"
                    >
                      <BaseIcon name="HeroAtSymbolIconOutline" class="h-5 w-5" />
                    </div>
                    <div
                      v-if="element.name === 'birthday'"
                      class="hidden rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white sm:inline-flex"
                    >
                      <BaseIcon name="HeroCakeIconOutline" class="h-5 w-5" />
                    </div>
                    <div
                      v-if="element.name === 'gender'"
                      class="hidden rounded-lg bg-pink-50 p-3 text-pink-700 ring-4 ring-white sm:inline-flex"
                    >
                      <BaseIcon name="HeroUserCircleIconOutline" class="h-5 w-5" />
                    </div>
                    <div
                      v-if="element.name === 'firstName'"
                      class="hidden rounded-lg bg-yellow-50 p-3 text-yellow-700 ring-4 ring-white sm:inline-flex"
                    >
                      <BaseIcon name="HeroIdentificationIconOutline" class="h-5 w-5" />
                    </div>
                    <p
                      class="truncate text-center text-xs font-medium text-foreground group-hover:text-foreground sm:pt-4 sm:text-sm"
                    >
                      {{ $t(`pages.crm.crmPage.blocks.drag.questions.${element.name}`) }}
                    </p>
                  </div>
                </template>
              </draggable>
            </div>
            <div class="col-span-2">
              <div
                class="list-group-item my-4 cursor-default overflow-hidden rounded-lg bg-white px-2 py-8 text-base sm:px-4"
              >
                <div class="flex items-center md:pl-4">
                  <span class="hidden rounded-lg bg-emerald-100 p-3 text-green-900 ring-4 ring-white sm:inline-flex">
                    <BaseIcon name="HeroChatBubbleBottomCenterTextIconOutline" class="h-5 w-5" />
                  </span>

                  <div class="ml-2 flex w-full items-center text-xs font-medium text-foreground sm:text-sm md:ml-5">
                    {{ $t('pages.crm.crmPage.blocks.drag.questions.optin.title') }}
                    <AppTooltip width="w-fit" tooltip-position="top" class="z-10 m-2">
                      <BaseIcon
                        name="IconoirInfoCircle"
                        class="ml-2 h-4 w-4 cursor-pointer text-foreground-secondary"
                      />
                      <template #tooltip-content>
                        <h2 class="m-2 mb-2 text-left text-lg font-bold text-foreground-disabled">
                          {{ $t('pages.crm.crmPage.blocks.drag.questions.optin.tooltip.title') }}
                        </h2>
                        <div class="m-2 flex flex-col gap-3">
                          <p class="w-80 text-left text-sm">
                            {{ $t('pages.crm.crmPage.blocks.drag.questions.optin.tooltip.description') }}
                          </p>
                        </div>
                      </template>
                    </AppTooltip>
                  </div>
                  <div class="mr-2 w-full text-right text-muted-foreground md:mr-10">
                    <BaseChip :text="$t('pages.crm.crmPage.blocks.drag.visits', { nb: 1 }, 1)">
                      <template #leading>
                        <Lock />
                      </template>
                    </BaseChip>
                  </div>
                </div>
              </div>
              <draggable
                :list="active"
                item-key="id"
                class="list-group"
                :class="[
                  active.length == 0
                    ? 'my-4 flex h-60 items-center justify-center rounded-sm border-2 border-dashed border-border bg-muted'
                    : ' min-h-full',
                  !showCRMEditButton ? 'pointer-events-none' : ''
                ]"
                group="people"
                ghost-class="moving"
                :animation="200"
                @change="save"
              >
                <template v-if="active.length == 0" #header>
                  <div class="w-max">
                    <div class="p-6">
                      <div class="text-center">
                        <span class="inline-flex rounded-full bg-muted p-3 text-muted-foreground">
                          <BaseIcon name="HeroMoonIconOutline" class="h-6 w-6 sm:h-10 sm:w-10" />
                        </span>
                      </div>
                      <h3 class="mb-1 pt-4 text-center text-sm font-bold text-muted-foreground sm:text-xl">
                        {{ $t('pages.crm.crmPage.blocks.drag.active.empty.title') }}
                      </h3>
                      <div class="text-center text-xs text-muted-foreground sm:text-base">
                        {{ $t('pages.crm.crmPage.blocks.drag.active.empty.description') }}
                      </div>
                    </div>
                  </div>
                </template>

                <template #item="{ element, index }">
                  <div
                    class="list-group-item my-4 cursor-move overflow-hidden rounded-lg border bg-white px-2 py-8 text-base hover:bg-accent sm:px-4"
                  >
                    <div class="flex items-center md:pl-4">
                      <span
                        v-if="element.name === 'email'"
                        class="hidden rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white sm:inline-flex"
                      >
                        <BaseIcon name="HeroAtSymbolIconOutline" class="h-5 w-5" />
                      </span>
                      <span
                        v-if="element.name === 'birthday'"
                        class="hidden rounded-lg bg-primary/5 p-3 text-primary ring-4 ring-white sm:inline-flex"
                      >
                        <BaseIcon name="HeroCakeIconOutline" class="h-5 w-5" />
                      </span>
                      <span
                        v-if="element.name === 'gender'"
                        class="hidden rounded-lg bg-pink-50 p-3 text-pink-700 ring-4 ring-white sm:inline-flex"
                      >
                        <BaseIcon name="HeroUserCircleIconOutline" class="h-5 w-5" />
                      </span>
                      <span
                        v-if="element.name === 'firstName'"
                        class="hidden rounded-lg bg-yellow-50 p-3 text-yellow-700 ring-4 ring-white sm:inline-flex"
                      >
                        <BaseIcon name="HeroIdentificationIconOutline" class="h-5 w-5" />
                      </span>
                      <div class="ml-2 w-full text-xs font-medium text-foreground sm:text-sm md:ml-5">
                        {{ $t(`pages.crm.crmPage.blocks.drag.questions.${element.name}`) }}
                      </div>
                      <div class="mr-2 w-full text-right text-muted-foreground md:mr-10">
                        <BaseChip
                          :text="
                            $t(
                              'pages.crm.crmPage.blocks.drag.visits',
                              { nb: 1 + (index + 1) * (questionInterval + 1) },
                              (index + 2) * questionInterval > 1 ? 2 : 1
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>

<script>
import { Lock, Minus, Plus } from 'lucide-vue-next'
import useLocalStorage from '@/composables/useLocalStorage'
import draggable from 'vuedraggable/src/vuedraggable'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import AppDownloadCsvButton from '@/components/ui/AppDownloadCsvButton.vue'

export default {
  components: {
    Lock,
    Minus,
    Plus,
    TheLoadingPage,
    draggable,
    AppDownloadCsvButton
  },
  setup() {
    const notification = useNotification()
    const { permissions, user } = useLocalStorage()

    return {
      notification,
      permissions,
      user
    }
  },
  data: () => {
    return {
      isLoaded: false,
      active: [],
      changes: false,
      changesInterval: false,
      questionInterval: null,
      questions: [],
      inactive: [],
      modal: {
        isOpen: false
      },
      pageTitle: {
        title: 'pages.crm.crmPage.title',
        description: 'pages.crm.crmPage.description'
      }
    }
  },
  computed: {
    crumbs() {
      return [
        {
          name: this.$t('pages.crm.crmPage.title')
        }
      ]
    },
    showCRMEditButton() {
      return this.permissions['crm.update']
    },
    showCRMExportButton() {
      // To update after backend work with crm.export
      return this.permissions['crm.read']
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      const requestMerchant = this.axios.get(`/merchants/${this.user.merchant.id}/configs`).then((response) => {
        this.questionInterval = Number(response.data.data.questionInterval)
      })

      const requestQuestions = this.axios
        .get('/enrichment-questions')
        .then((response) => {
          response.data.data.forEach((question) => {
            var processedQuestion = {
              name: question.slug,
              id: question.id,
              questionMerchant: question.questionMerchant
            }

            if (question.questionMerchant?.isActive) {
              this.active.push(processedQuestion)
            } else {
              this.inactive.push(processedQuestion)
            }
          })

          this.active.sort(function (a, b) {
            return a.questionMerchant.position - b.questionMerchant.position
          })
        })
        .finally(() => {
          this.isLoaded = true
        })

      this.axios.all([requestMerchant, requestQuestions]).then(
        this.axios.spread(() => {
          this.isLoaded = true
        })
      )
    },
    save() {
      if (this.changesInterval) {
        this.axios
          .put(`/merchants/${this.user.merchant.id}/configs`, {
            data: {
              questionInterval: this.questionInterval
            }
          })
          .then(() => {
            this.notification.create('success', {
              text: 'Modifications enregistrées'
            })
          })
      } else {
        var questions = []
        this.active.forEach((question, index) => {
          questions.push({
            id: question.questionMerchant === null ? null : question.questionMerchant.id,
            merchantId: this.user.merchant.id,
            isActive: true,
            points: 0,
            position: index + 1,
            questionId: question.id
          })
        })
        this.inactive.forEach((question) => {
          questions.push({
            id: question.questionMerchant === null ? null : question.questionMerchant.id,
            merchantId: this.user.merchant.id,
            isActive: false,
            points: 0,
            position: 1,
            questionId: question.id
          })
        })

        this.axios
          .patch('/enrichment-questions/question-merchants', {
            configs: questions
          })
          .then(() => {
            this.notification.create('success', {
              text: 'Modifications enregistrées'
            })
          })
      }
    },
    interval(i) {
      var newInterval = Number(this.questionInterval) + Number(i)
      if (newInterval >= 0) {
        this.questionInterval = newInterval
        this.changesInterval = true
        this.save()
      }
    }
  }
}
</script>
<style scoped>
.moving {
  border: solid #5324bc 2px;
  background-color: rgba(209, 213, 219, 0.2);
}
</style>
